import clsx from 'clsx';
import { enqueueSnackbar } from 'notistack';
import { Activity, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import {
  IconTrash,
  IconSearch,
  IconRestore,
  IconAdjustments,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import CustomTable from '@/components/Table';
import { ERestoreEntityType } from '@/enum';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import {
  restoreRecord,
  getDeletedRecords,
  permanentlyDeleteRecord,
} from '@/service/settingsService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { IDeletedRecord } from '@/types';

import DeletedRecordsFilterDrawer, {
  type TDeletedRecordsFilters,
} from './DeletedRecordsFilterDrawer';

// ─── Type Config ─────────────────────────────────────────────────────────────

const getTypeConfig = (type: string): { label: string; className: string } => {
  const typeMapping: Record<string, { label: string; className: string }> = {
    teacher: {
      label: 'settings.deleted-records.types.teacher',
      className: 'bg-yellow-100 text-yellow-600',
    },
    student: {
      label: 'settings.deleted-records.types.student',
      className: 'bg-green-50 text-green-600',
    },
    class: {
      label: 'settings.deleted-records.types.class',
      className: 'bg-red-50 text-red-500',
    },
    lesson: {
      label: 'settings.deleted-records.types.lesson',
      className: 'bg-orange-50 text-orange-600',
    },
  };

  const lowerType = type.toLowerCase();
  return (
    typeMapping[lowerType] || {
      label: type || '-',
      className: 'bg-neutral-50 text-neutral-secondary',
    }
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

const DeletedRecords = () => {
  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();
  const { isTableLoading, setTableLoading } = useSkeletonStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [deletedRecords, setDeletedRecords] = useState<IDeletedRecord[]>([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<TDeletedRecordsFilters>({
    type: undefined,
    startDate: null,
    endDate: null,
  });

  const [sortColumn, setSortColumn] = useState('deleted_on');
  const [sortDirection, setSortDirection] = useState('DESC');

  const { register, watch } = useForm();
  const searchTerm = watch('search') || '';
  const debouncedSearch = useDebounce(searchTerm, 300);

  const [restoreModal, setRestoreModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<IDeletedRecord | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFilterApplied =
    appliedFilters.type !== undefined ||
    !!appliedFilters.startDate ||
    !!appliedFilters.endDate;

  const isEmptyList =
    !isTableLoading &&
    deletedRecords.length === 0 &&
    !debouncedSearch &&
    !isFilterApplied;

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchDeletedRecords = useCallback(async () => {
    setTableLoading(true);
    try {
      const query: Record<string, unknown> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        order: sortColumn ? { [sortColumn]: sortDirection } : undefined,
      };

      if (debouncedSearch) query.search = debouncedSearch;
      if (appliedFilters?.type) query.type = appliedFilters.type;
      if (appliedFilters?.startDate) {
        query.start_date = Math.floor(
          new Date(appliedFilters?.startDate).getTime() / 1000
        );
      }
      if (appliedFilters?.endDate) {
        query.end_date = Math.floor(
          new Date(appliedFilters?.endDate).getTime() / 1000
        );
      }

      const res = await getDeletedRecords(query);
      if (res?.data) {
        setDeletedRecords(res.data);
        setTotal(res.total || 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTableLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    appliedFilters,
    sortColumn,
    sortDirection,
  ]);

  useEffect(() => {
    fetchDeletedRecords();
  }, [fetchDeletedRecords]);

  // ── Sort ─────────────────────────────────────────────────────────────────────

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortColumn('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortColumn(key);
      setSortDirection('ASC');
    }
  };

  // ── Restore ──────────────────────────────────────────────────────────────────

  const confirmRestore = async () => {
    if (!selectedRecord) return;
    setIsSubmitting(true);
    try {
      const response = await restoreRecord(selectedRecord.id);
      if (response?.status) {
        enqueueSnackbar(
          response.message || formatText('common.actions.success'),
          { variant: 'success' }
        );
        fetchDeletedRecords();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
      setRestoreModal(false);
      setSelectedRecord(null);
    }
  };

  const confirmPermanentDelete = async () => {
    if (!selectedRecord) return;
    setIsSubmitting(true);
    try {
      const recordType =
        ERestoreEntityType[
          selectedRecord.type.toUpperCase() as keyof typeof ERestoreEntityType
        ];
      const response = await permanentlyDeleteRecord(
        selectedRecord.id,
        recordType
      );
      if (response?.status) {
        enqueueSnackbar(
          response.message || formatText('common.actions.success'),
          { variant: 'success' }
        );
        fetchDeletedRecords();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
      setDeleteModal(false);
      setSelectedRecord(null);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────────

  const columns = [
    {
      title: 'settings.deleted-records.columns.names',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      handleSort: () => handleSort('name'),
      render: (record: IDeletedRecord) => (
        <div className="flex items-center gap-3">
          <UserAvatarList
            user={record.name}
            image={record.url ?? ''}
            className="w-10 h-10"
          />
          <span className="text-sm font-medium text-neutral-primary">
            {record.name}
          </span>
        </div>
      ),
    },
    {
      title: 'settings.deleted-records.columns.type',
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      handleSort: () => handleSort('type'),
      render: (record: IDeletedRecord) => {
        const config = getTypeConfig(record.type);
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${config.className}`}
          >
            {formatText(config.label)}
          </span>
        );
      },
    },
    {
      title: 'settings.deleted-records.columns.deleted-on',
      dataIndex: 'deleted_on',
      key: 'deleted_on',
      sorter: true,
      handleSort: () => handleSort('deleted_on'),
      render: (record: IDeletedRecord) =>
        record.deleted_on ? formatDate(record.deleted_on) : '-',
    },
    {
      title: 'common.actions.action',
      dataIndex: 'action',
      key: 'action',
      align: 'center' as const,
      render: (record: IDeletedRecord) => (
        <ActionDropdown
          actions={[
            {
              key: 'restore',
              label: 'settings.deleted-records.actions.restore-btn',
              icon: <IconRestore size={20} />,
              onClick: () => {
                setSelectedRecord(record);
                setRestoreModal(true);
              },
            },
            {
              key: 'delete-permanently',
              label: 'settings.deleted-records.actions.delete-btn',
              icon: <IconTrash size={20} />,
              isDanger: true,
              onClick: () => {
                setSelectedRecord(record);
                setDeleteModal(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-6" data-testid="deleted-records-container">
      {/* Search & Filter Bar */}
      {!isEmptyList && (
        <div className="flex items-center gap-2.5 ps-1">
          <Input
            name="search"
            register={register}
            placeholder={formatText(
              'settings.deleted-records.search-placeholder'
            )}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-hover" />,
            }}
            className="max-w-70"
            inputClassName="py-3"
            dataTestId="deleted-records-search-input"
          />
          <div className="relative">
            <Button
              variant="outline"
              label={formatText('common.actions.filter')}
              icon={<IconAdjustments size={20} />}
              className={clsx(
                'h-11.5',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsFilterDrawerOpen(true)}
              dataTestId="deleted-records-filter-button"
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <CustomTable
        dataSource={deletedRecords}
        columns={columns}
        total={total}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        rowKey="id"
        fieldName={sortColumn}
        sortDirection={sortDirection}
        isEmpty={isEmptyList}
        emptyTitle={formatText('settings.deleted-records.not-found-title')}
        emptyDescription={formatText('settings.deleted-records.not-found-desc')}
        isEmptySearch={
          deletedRecords.length === 0 && (!!debouncedSearch || isFilterApplied)
        }
        isLoading={isTableLoading}
        dataTestId="deleted-records-table"
      />

      {/* Restore Confirmation Modal */}
      <Activity mode={restoreModal ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={restoreModal}
          setIsOpen={setRestoreModal}
          title={formatText('settings.deleted-records.actions.restore-title')}
          descriptionPrefix={formatText(
            'settings.deleted-records.actions.restore-confirm'
          )}
          descriptionSuffix={formatText(
            'settings.deleted-records.actions.restore-confirm-suffix'
          )}
          confirmLabel={formatText(
            'settings.deleted-records.actions.restore-btn'
          )}
          onConfirm={confirmRestore}
          isLoading={isSubmitting}
          dataTestId="restore-record-modal"
        />
      </Activity>

      {/* Permanent Delete Confirmation Modal */}
      <Activity mode={deleteModal ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={deleteModal}
          setIsOpen={setDeleteModal}
          title={formatText(
            'settings.deleted-records.actions.delete-permanently-title'
          )}
          descriptionPrefix={formatText(
            'settings.deleted-records.actions.delete-permanent-confirm'
          )}
          descriptionSuffix={formatText(
            'settings.deleted-records.actions.delete-permanent-confirm-suffix'
          )}
          confirmLabel={formatText(
            'settings.deleted-records.actions.delete-btn'
          )}
          onConfirm={confirmPermanentDelete}
          isLoading={isSubmitting}
          isDestructive
          dataTestId="permanent-delete-modal"
        />
      </Activity>

      {/* Filter Drawer */}
      <Activity mode={isFilterDrawerOpen ? 'visible' : 'hidden'}>
        <DeletedRecordsFilterDrawer
          isOpen={isFilterDrawerOpen}
          setIsOpen={setIsFilterDrawerOpen}
          initialFilters={appliedFilters}
          onApplyFilters={(filters) => {
            setAppliedFilters(filters);
            setCurrentPage(1);
          }}
          dataTestId="deleted-records-filter-drawer"
        />
      </Activity>
    </div>
  );
};

export default DeletedRecords;
